import { createErrorFactory } from '@quotientjs/error';

/**
 * Formats for API key validation
 */ // Key Prefixes
const PUBLIC_KEY_PREFIX = "pk_";
// Key Lengths
const MIN_API_KEY_LENGTH = 20 // Minimum characters after prefix
;
const MAX_API_KEY_LENGTH = 40 // Maximum characters after prefix
;
// Validation Patterns
const PUBLIC_KEY_PATTERN = new RegExp(`^${PUBLIC_KEY_PREFIX}[a-zA-Z0-9]{${MIN_API_KEY_LENGTH},${MAX_API_KEY_LENGTH}}$`);
/**
 * Validates that a public key has the proper format
 */ function isValidPublicApiKeyFormat(key) {
    return PUBLIC_KEY_PATTERN.test(key);
}
function isPublicApiKey(key) {
    return isValidPublicApiKeyFormat(key);
}

var version = "0.21.0";
var packageJson = {
	version: version};

/** The version of @quotientjs/core, read from package.json. */ const SDK_VERSION = packageJson.version;

const DEFAULT_API_URL = "https://www.getquotient.ai";
const SDK_VERSION_HEADER = "X-Quotient-Sdk-Version";
const ApiClientError = createErrorFactory({
    InvalidEndpoint: [
        "Invalid endpoint format: {endpoint}",
        {
            endpoint: String
        }
    ],
    InvalidApiKey: "Invalid API key",
    InvalidApiKeyType: "Invalid API key type",
    InvalidApiUrl: [
        "Invalid API URL: {url}",
        {
            url: String
        }
    ],
    ApiResponseNotJson: "API response is not JSON",
    ApiError: [
        "{message}",
        {
            message: String,
            status: Number,
            data: Object
        }
    ]
});
/**
 * Formats SDK version entries for the X-Quotient-Sdk-Version header:
 * `@quotientjs/react:1.2.3;@quotientjs/core:4.5.6;`
 */ function formatSdkVersions(entries) {
    return entries.map((entry)=>`${entry.name}:${entry.version};`).join("");
}
/**
 * Base client for making API requests
 * This is a clean base class with no environment-specific logic
 */ class ApiClient {
    /**
   * NOTE (amiller68): this is async to accommodate for browser-specific
   *  storage providers that may need to fetch values asynchronously.
   *  This shouldn't really be a big deal for non-async storage providers + server-side
   *  since it's called within the call method, which is already async.
   *
   * Get headers for requests
   * Override this method in subclasses to add custom headers
   */ async getHeaders() {
        return {
            "Content-Type": "application/json",
            [SDK_VERSION_HEADER]: formatSdkVersions(this.sdkVersions)
        };
    }
    /**
   * Generic method to execute any API request.
   *
   * `fetchOptions` lets callers override per-request fetch config such as
   * `cache` and `next: { revalidate, tags }` for Next.js ISR. SDK-owned
   * fields (method, body, keepalive) and SDK headers (Authorization,
   * Content-Type, platform) always win — callers cannot drop auth by
   * passing custom headers.
   */ async call(request, fetchOptions) {
        const { method, endpoint, params, body, auth, options } = request;
        const { beacon } = options || {};
        // check that the endpoint is the form of '/<path>'
        if (endpoint.charAt(0) !== "/") {
            throw ApiClientError.InvalidEndpoint({
                endpoint
            });
        }
        // Build the URL with query parameters. Spec-compliant URL#toString
        // appends a trailing slash to a bare origin, but non-compliant
        // implementations (e.g. React Native's URL polyfill) and base URLs with
        // a path suffix don't — normalize so the path never fuses with the host
        // ("https://www.getquotient.aiapi/v0/...").
        const base = this.baseUrl.toString();
        let url = `${base}${base.endsWith("/") ? "" : "/"}api/v0${endpoint}`;
        if (params) {
            const searchParams = new URLSearchParams();
            Object.keys(params).forEach((key)=>{
                const value = params[key];
                if (value !== undefined) {
                    // Handle arrays by appending multiple entries with the same key
                    if (Array.isArray(value)) {
                        value.forEach((item)=>{
                            searchParams.append(key, String(item));
                        });
                    } else {
                        searchParams.append(key, String(value));
                    }
                }
            });
            const queryString = searchParams.toString();
            if (queryString) {
                url += `?${queryString}`;
            }
        }
        // Get headers
        const headers = await this.getHeaders();
        // Add authorization header if API key is available
        if (this.apiKey && auth.required) {
            headers["Authorization"] = `Bearer ${this.apiKey}`;
        }
        // Configure fetch options. SDK-owned fields (method, body, keepalive)
        // and SDK headers always win — caller can override cache, next, signal,
        // etc. but cannot drop auth or change the HTTP verb. `cache` defaults to
        // "no-store" but the caller can opt out (e.g. for Next.js ISR).
        const { method: _ignoredCallerMethod, body: _ignoredCallerBody, keepalive: _ignoredCallerKeepalive, cache: callerCache, headers: callerHeaders, ...restCallerOptions } = fetchOptions != null ? fetchOptions : {};
        const mergedFetchOptions = {
            ...restCallerOptions,
            method,
            cache: callerCache != null ? callerCache : "no-store",
            headers: {
                ...callerHeaders,
                ...headers
            },
            ...beacon ? {
                keepalive: true
            } : {},
            // Include body for non-GET requests
            ...method !== "GET" && body ? {
                body: JSON.stringify(body)
            } : {}
        };
        // If this request should use beacon
        if (beacon) {
            // fire and forget
            fetch(url, mergedFetchOptions);
            return true; // Type assertion needed for conditional return type
        }
        // Execute the request with regular fetch if not using beacon or beacon failed
        const response = await fetch(url, mergedFetchOptions);
        // Parse the response
        let data;
        try {
            data = await response.json();
        } catch (error) {
            throw ApiClientError.ApiResponseNotJson({});
        }
        // Handle error responses
        if (!response.ok) {
            const errorMessage = data.error || data.message || "Unknown API error";
            throw ApiClientError.ApiError({
                message: errorMessage,
                status: response.status,
                data: data
            });
        }
        return data;
    }
    /**
   * Create a new API client
   */ constructor(options){
        const supportedKeyTypes = options.supportedKeyTypes || [
            "public",
            "private"
        ];
        const keyType = isPublicApiKey(options.apiKey) ? "public" : "private";
        if (!supportedKeyTypes.includes(keyType)) {
            throw ApiClientError.InvalidApiKeyType({});
        }
        try {
            this.baseUrl = new URL(options.baseUrl || DEFAULT_API_URL);
        } catch (error) {
            var _options_baseUrl;
            const strUrl = ((_options_baseUrl = options.baseUrl) == null ? void 0 : _options_baseUrl.toString()) || DEFAULT_API_URL;
            throw ApiClientError.InvalidApiUrl({
                url: strUrl
            });
        }
        this.apiKey = options.apiKey;
        var _options_sdkVersions;
        this.sdkVersions = [
            ...(_options_sdkVersions = options.sdkVersions) != null ? _options_sdkVersions : [],
            {
                name: "@quotientjs/core",
                version: SDK_VERSION
            }
        ];
    }
}

/**
 * Base abstract class for all API requests
 */ class ApiRequest {
}

/**
 * Tracks a client-originated analytics event against `/analytics/web`.
 * Fire-and-forget via `beacon`, like other browser analytics.
 */ class ClientTrackEventRequest extends ApiRequest {
    constructor(payload){
        super(), this.method = "POST", this.endpoint = "/analytics/web", this.auth = {
            required: true
        }, this.options = {
            beacon: true
        };
        this.body = payload;
    }
}

/** Tracks a server-originated analytics event against `/analytics/server`. */ class ServerTrackEventRequest extends ApiRequest {
    constructor(event){
        super(), this.method = "POST", this.endpoint = "/analytics/server", this.auth = {
            required: true
        };
        this.body = event;
    }
}

class WhoAmIRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/whoami", // Authentication requirements
        this.auth = {
            required: true
        };
    }
}

/**
 * Upsert a person with their email subscription status
 */ class UpsertPersonRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/audience/people", // Authentication requirements
        this.auth = {
            required: true
        };
        this.body = {
            emailAddress: options.emailAddress,
            emailSubscriptionStatus: options.emailSubscriptionStatus,
            firstName: options.firstName,
            lastName: options.lastName,
            mainPhoneNumber: options.mainPhoneNumber,
            jobTitle: options.jobTitle,
            leadScore: options.leadScore,
            companyId: options.companyId,
            lists: options.lists,
            events: options.events,
            properties: options.properties
        };
    }
}

/**
 * Upsert a company by domain.
 *
 * The `domain` field is required and acts as the upsert key within a business.
 * - Zero matches → creates a new company.
 * - Exactly one match → updates it.
 * - Two or more matches → the request fails with a 409 response (body
 *   includes `domain`, `matchCount`, and `matchingCompanyIds`). Multiple
 *   distinct companies legitimately sharing a domain is expected (different
 *   country entities of a multinational, franchisee/licensee networks,
 *   holding companies), so this never guesses which one to update —
 *   disambiguate by upserting/updating a specific company by id instead.
 */ class UpsertCompanyRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/audience/companies", this.auth = {
            required: true
        };
        this.body = {
            domain: options.domain,
            name: options.name,
            description: options.description,
            industries: options.industries,
            revenue: options.revenue,
            revenueCurrency: options.revenueCurrency,
            totalEmployees: options.totalEmployees,
            address1: options.address1,
            address2: options.address2,
            city: options.city,
            regionCode: options.regionCode,
            country: options.country,
            zip: options.zip,
            socialLinkFacebook: options.socialLinkFacebook,
            socialLinkInstagram: options.socialLinkInstagram,
            socialLinkTwitter: options.socialLinkTwitter,
            socialLinkLinkedIn: options.socialLinkLinkedIn,
            socialLinkAngelList: options.socialLinkAngelList,
            properties: options.properties
        };
    }
}

class UpsertListRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/audience/lists", this.auth = {
            required: true
        };
        this.body = {
            name: options.name,
            slug: options.slug,
            description: options.description
        };
    }
}

class ListListsRequest extends ApiRequest {
    constructor(options = {}){
        super(), this.method = "GET", this.endpoint = "/audience/lists", this.auth = {
            required: true
        };
        this.params = {
            search: options.search,
            page: options.page,
            limit: options.limit
        };
    }
}

class GetListRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/audience/lists/:slug", this.auth = {
            required: true
        };
        this.endpoint = `/audience/lists/${options.slug}`;
    }
}

class ListPeopleInListRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/audience/lists/:slug/people", this.auth = {
            required: true
        };
        this.endpoint = `/audience/lists/${options.listSlug}/people`;
        this.params = {
            search: options.search,
            page: options.page,
            limit: options.limit
        };
    }
}

class AddPeopleToListRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/audience/lists/:slug/people", this.auth = {
            required: true
        };
        this.endpoint = `/audience/lists/${options.listSlug}/people`;
        this.body = {
            people: options.people
        };
    }
}

class RemovePeopleFromListRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "DELETE", this.endpoint = "/audience/lists/:slug/people", this.auth = {
            required: true
        };
        this.endpoint = `/audience/lists/${options.listSlug}/people`;
        this.body = {
            people: options.people
        };
    }
}

class ListPropertiesRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/audience/properties", this.auth = {
            required: true
        };
        this.params = {
            entityType: options.entityType
        };
    }
}

class CreatePropertyRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/audience/properties", this.auth = {
            required: true
        };
        this.body = {
            entityType: options.entityType,
            id: options.id,
            displayName: options.displayName,
            datatype: options.datatype,
            allowedValues: options.allowedValues,
            readOnly: options.readOnly
        };
    }
}

class UpdatePropertyRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "PATCH", this.endpoint = "/audience/properties", this.auth = {
            required: true
        };
        // Encoded: new ids are alphanumeric, but legacy/CRM-created ids aren't
        // guaranteed to be URL-safe.
        this.endpoint = `/audience/properties/${encodeURIComponent(options.propertyId)}`;
        this.body = {
            entityType: options.entityType,
            displayName: options.displayName,
            readOnly: options.readOnly,
            addAllowedValues: options.addAllowedValues
        };
    }
}

class GetBlogRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/blog/{slug}", this.auth = {
            required: true
        };
        this.endpoint = this.endpoint.replace("{slug}", options.slug);
        if (options.rawHtml) {
            this.params = {
                rawHtml: options.rawHtml
            };
        }
    }
}

class ListBlogsRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/blog/list", this.auth = {
            required: true
        };
        this.params = {
            search: options.search,
            page: options.page,
            limit: options.limit,
            authorIds: options.authorIds,
            tagIds: options.tagIds,
            statuses: options.statuses
        };
    }
}

class ListAuthorsRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/blog/authors", this.auth = {
            required: true
        };
        this.params = {
            search: options.search,
            page: options.page,
            limit: options.limit
        };
    }
}

class ListTagsRequest extends ApiRequest {
    constructor(){
        super(), this.method = "GET", this.endpoint = "/blog/tags", this.auth = {
            required: true
        };
    }
}

class TriggerFlowRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/flow/{flowId}/trigger", this.auth = {
            required: true
        };
        this.endpoint = this.endpoint.replace("{flowId}", options.flowId);
        this.body = {
            personId: options.personId,
            metadata: options.metadata
        };
    }
}

class LsMemoryRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/memory", this.auth = {
            required: true
        };
        const cleanPath = options.path.startsWith("/") ? options.path.slice(1) : options.path;
        this.endpoint = cleanPath ? `/memory/${cleanPath}` : "/memory";
        this.params = {
            ls: true,
            ...options.deep ? {
                deep: true
            } : {}
        };
    }
}

class CatMemoryRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/memory", this.auth = {
            required: true
        };
        const cleanPath = options.path.startsWith("/") ? options.path.slice(1) : options.path;
        this.endpoint = cleanPath ? `/memory/${cleanPath}` : "/memory";
    }
}

class WriteMemoryRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/memory", this.auth = {
            required: true
        };
        const cleanPath = options.path.startsWith("/") ? options.path.slice(1) : options.path;
        this.endpoint = cleanPath ? `/memory/${cleanPath}` : "/memory";
        this.body = {
            title: options.title,
            content: options.content,
            tags: options.tags,
            pinned: options.pinned
        };
    }
}

class MkdirMemoryRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/memory", this.auth = {
            required: true
        };
        const cleanPath = options.path.startsWith("/") ? options.path.slice(1) : options.path;
        this.endpoint = cleanPath ? `/memory/${cleanPath}` : "/memory";
        this.body = {
            name: options.name
        };
    }
}

class RmMemoryRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "DELETE", this.endpoint = "/memory", this.auth = {
            required: true
        };
        const cleanPath = options.path.startsWith("/") ? options.path.slice(1) : options.path;
        this.endpoint = cleanPath ? `/memory/${cleanPath}` : "/memory";
    }
}

class SearchMemoryRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/memory/search", this.auth = {
            required: true
        };
        this.body = {
            query: options.query,
            ...options.tags && options.tags.length > 0 ? {
                tags: options.tags
            } : {}
        };
    }
}

export { AddPeopleToListRequest, ApiClient, ApiClientError, CatMemoryRequest, ClientTrackEventRequest, CreatePropertyRequest, GetBlogRequest, GetListRequest, ListAuthorsRequest, ListBlogsRequest, ListListsRequest, ListPeopleInListRequest, ListPropertiesRequest, ListTagsRequest, LsMemoryRequest, MkdirMemoryRequest, RemovePeopleFromListRequest, RmMemoryRequest, SearchMemoryRequest, ServerTrackEventRequest, TriggerFlowRequest, UpdatePropertyRequest, UpsertCompanyRequest, UpsertListRequest, UpsertPersonRequest, WhoAmIRequest, WriteMemoryRequest };
